"use strict";
// Copyright (C) 2017-2026 Smart code 203358507
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var EPG_1 = require("stremio/common/EPG");
var TRANSITION_DURATION = 300;
var clampScale = function (scale) { return Math.max(EPG_1.EPG_MIN_PIXELS_PER_HOUR, Math.min(EPG_1.EPG_MAX_PIXELS_PER_HOUR, scale)); };
var useScheduleViewport = function (start, visible) {
    var viewportRef = (0, react_1.useRef)(null);
    var anchorRef = (0, react_1.useRef)(null);
    var animationRef = (0, react_1.useRef)(null);
    var originRef = (0, react_1.useRef)(start);
    var _a = (0, react_1.useState)({ left: 0, width: 0 }), viewport = _a[0], setViewport = _a[1];
    var _b = (0, react_1.useState)(EPG_1.EPG_PIXELS_PER_HOUR), scale = _b[0], setScale = _b[1];
    var measure = (0, react_1.useCallback)(function () {
        var element = viewportRef.current;
        if (element && element.clientWidth > 0)
            setViewport(function (previous) { return previous.left === element.scrollLeft && previous.width === element.clientWidth
                ? previous : { left: element.scrollLeft, width: element.clientWidth }; });
    }, []);
    var moveTo = function (time, nextScale) {
        var element = viewportRef.current;
        if (!element)
            return;
        if (animationRef.current !== null)
            cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
        var value = clampScale(nextScale);
        if (element.clientWidth === 0) {
            anchorRef.current = time;
            setScale(value);
            return;
        }
        var fromLeft = element.scrollLeft;
        var toLeft = Math.max(0, (time - start) * value / EPG_1.HOUR_IN_MS);
        var apply = function (progress) {
            var currentScale = scale + (value - scale) * progress;
            var left = Math.round(fromLeft + (toLeft - fromLeft) * progress);
            if (value !== scale) {
                anchorRef.current = start + left * EPG_1.HOUR_IN_MS / currentScale;
                setScale(currentScale);
                setViewport({ left: left, width: element.clientWidth });
            }
            else {
                element.scrollLeft = left;
                measure();
            }
        };
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
            apply(1);
            return;
        }
        var began = performance.now();
        var animate = function (now) {
            var progress = Math.min(1, (now - began) / TRANSITION_DURATION);
            // Interpolate both coordinates together so every programme moves continuously while zooming.
            apply(1 - Math.pow(1 - progress, 3));
            if (progress < 1)
                animationRef.current = requestAnimationFrame(animate);
            else
                animationRef.current = null;
        };
        animationRef.current = requestAnimationFrame(animate);
    };
    (0, react_1.useEffect)(function () { return function () {
        if (animationRef.current !== null)
            cancelAnimationFrame(animationRef.current);
    }; }, []);
    (0, react_1.useLayoutEffect)(function () {
        var _a;
        var element = viewportRef.current;
        if (!element || element.clientWidth === 0)
            return;
        if (originRef.current !== start) {
            (_a = anchorRef.current) !== null && _a !== void 0 ? _a : (anchorRef.current = originRef.current + element.scrollLeft * EPG_1.HOUR_IN_MS / scale);
            originRef.current = start;
        }
        if (anchorRef.current !== null) {
            element.scrollLeft = Math.max(0, (anchorRef.current - start) * scale / EPG_1.HOUR_IN_MS);
            anchorRef.current = null;
        }
        measure();
    }, [scale, start, visible, measure]);
    (0, react_1.useEffect)(function () {
        var element = viewportRef.current;
        if (!element)
            return;
        var frame = null;
        var onScroll = function () {
            if (frame === null)
                frame = requestAnimationFrame(function () { frame = null; measure(); });
        };
        var onInput = function () {
            if (animationRef.current !== null)
                cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
            anchorRef.current = null;
        };
        var onWheel = function (event) {
            if (event.ctrlKey)
                return;
            onInput();
            if (Math.abs(event.deltaX) >= Math.abs(event.deltaY))
                return;
            var previous = element.scrollLeft;
            element.scrollLeft += event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? element.clientWidth : 1);
            if (element.scrollLeft !== previous)
                event.preventDefault();
        };
        var observer = new ResizeObserver(measure);
        observer.observe(element);
        element.addEventListener('scroll', onScroll, { passive: true });
        element.addEventListener('wheel', onWheel, { passive: false });
        element.addEventListener('pointerdown', onInput, { passive: true });
        element.addEventListener('keydown', onInput);
        measure();
        return function () {
            observer.disconnect();
            element.removeEventListener('scroll', onScroll);
            element.removeEventListener('wheel', onWheel);
            element.removeEventListener('pointerdown', onInput);
            element.removeEventListener('keydown', onInput);
            if (frame !== null)
                cancelAnimationFrame(frame);
        };
    }, [measure]);
    var zoom = function (factor) {
        var element = viewportRef.current;
        if (!element)
            return;
        var value = clampScale(scale * factor);
        var center = start + (element.scrollLeft + element.clientWidth / 2) * EPG_1.HOUR_IN_MS / scale;
        moveTo(center - element.clientWidth * EPG_1.HOUR_IN_MS / value / 2, value);
    };
    var focus = function (program, fit) {
        if (fit === void 0) { fit = false; }
        var element = viewportRef.current;
        if (!element)
            return;
        var from = program.startTime.getTime();
        var to = program.endTime.getTime();
        var value = fit ? clampScale(element.clientWidth * EPG_1.HOUR_IN_MS / Math.max(EPG_1.HOUR_IN_MS / 2, (to - from) * 1.2)) : scale;
        var time = fit ? (from + to - element.clientWidth * EPG_1.HOUR_IN_MS / value) / 2 : from;
        moveTo(time, value);
    };
    var showTime = function (time) {
        var _a;
        var element = viewportRef.current;
        if (!element)
            return;
        var width = element.clientWidth || ((_a = element.parentElement) === null || _a === void 0 ? void 0 : _a.clientWidth) || 0;
        moveTo(time - width * EPG_1.HOUR_IN_MS / scale / 2, scale);
    };
    return { viewportRef: viewportRef, viewport: viewport, scale: scale, zoom: zoom, focus: focus, showTime: showTime, canZoomOut: scale > EPG_1.EPG_MIN_PIXELS_PER_HOUR, canZoomIn: scale < EPG_1.EPG_MAX_PIXELS_PER_HOUR };
};
exports.default = useScheduleViewport;
